import * as THREE from "three";

const DEFAULT_PILLOW_SEGMENTS: [number, number, number] = [20, 20, 2];
const DEFAULT_SOFT_BOX_SEGMENTS: [number, number, number] = [16, 16, 4];
/**
 * The rim keeps a fraction of the thickness instead of collapsing to zero, so
 * the top and bottom shells never end up coplanar and z-fight along the seam.
 */
const RIM_THICKNESS_RATIO = 0.22;
/** How far the plan outline is rounded in at the corners. */
const CORNER_ROUNDING = 0.28;
/** Wrinkle depth as a share of the pillow thickness, so it scales with size. */
const WRINKLE_DEPTH_RATIO = 0.05;
const WRINKLE_FREQUENCY = 5.5;

/**
 * Tunable shape of a puffed box. Defaults reproduce the lounge chair pillow;
 * bedding passes overrides to read squarer or firmer.
 */
export type PillowShapeOptions = {
  /** How far the plan corners pull inward, 0..1. */
  cornerRounding?: number;
  /**
   * Corner blend falloff. Higher powers leave the edges straight and round
   * only the corner tips, so the outline stays square instead of oval.
   */
  cornerPower?: number;
  /** Fraction of the thickness kept at the rim; higher reads plumper. */
  rimRatio?: number;
  /** Dome falloff exponent; lower flattens the top into a cushion. */
  puffExponent?: number;
  /** Wrinkle depth as a share of the thickness; 0 disables wrinkles. */
  wrinkleDepthRatio?: number;
  wrinkleFrequency?: number;
  /**
   * Optional rounded-rectangle outline. Values around 6-10 keep long edges
   * straight while easing only the corners; unset preserves the lounge-pillow
   * pinch used by existing upholstered furniture.
   */
  outlineExponent?: number;
  /** Smooth normals across the box-face seams for a continuous fabric shell. */
  smoothSeams?: boolean;
};

type ResolvedPuffShape = Required<PillowShapeOptions>;

const PILLOW_SHAPE: ResolvedPuffShape = {
  cornerRounding: CORNER_ROUNDING,
  cornerPower: 2,
  rimRatio: RIM_THICKNESS_RATIO,
  puffExponent: 0.5,
  wrinkleDepthRatio: WRINKLE_DEPTH_RATIO,
  wrinkleFrequency: WRINKLE_FREQUENCY,
  outlineExponent: 0,
  smoothSeams: false,
};

/** Maps a square grid into a superellipse without shortening its long edges. */
function roundedRectanglePoint(
  normalizedX: number,
  normalizedY: number,
  exponent: number,
): [number, number] {
  const squareRadius = Math.max(
    Math.abs(normalizedX),
    Math.abs(normalizedY),
  );
  if (squareRadius < 1e-9) return [0, 0];

  const directionX = normalizedX / squareRadius;
  const directionY = normalizedY / squareRadius;
  const boundaryScale = Math.pow(
    Math.pow(Math.abs(directionX), exponent) +
      Math.pow(Math.abs(directionY), exponent),
    -1 / exponent,
  );

  return [
    directionX * squareRadius * boundaryScale,
    directionY * squareRadius * boundaryScale,
  ];
}

/**
 * Puffs a segmented box: rounds the plan corners, inflates the centre easing
 * down to a soft rim, and optionally adds subtle surface wrinkles.
 */
function createPuffedBoxGeometry(
  width: number,
  depth: number,
  thickness: number,
  segments: [number, number, number],
  shape: ResolvedPuffShape,
): THREE.BufferGeometry {
  const [segmentX, segmentY, segmentZ] = segments;
  const geometry = new THREE.BoxGeometry(
    width,
    depth,
    thickness,
    segmentX,
    segmentY,
    segmentZ,
  );
  const position = geometry.attributes.position;
  const halfWidth = width / 2;
  const halfDepth = depth / 2;
  const wrinkleDepth = thickness * shape.wrinkleDepthRatio;

  for (let index = 0; index < position.count; index++) {
    const x = position.getX(index);
    const y = position.getY(index);
    const z = position.getZ(index);
    const normalizedX = halfWidth > 0 ? x / halfWidth : 0;
    const normalizedY = halfDepth > 0 ? y / halfDepth : 0;

    if (shape.outlineExponent > 0) {
      // Bed pillows need a recognisably rectangular envelope. A superellipse
      // preserves the full edge lengths and rounds only the corner transitions.
      const [outlinedX, outlinedY] = roundedRectanglePoint(
        normalizedX,
        normalizedY,
        shape.outlineExponent,
      );
      position.setX(index, outlinedX * halfWidth);
      position.setY(index, outlinedY * halfDepth);
    } else {
      // Lounge pillows keep their more relaxed, pinched cushion outline.
      const cornerPull =
        shape.cornerRounding *
        Math.pow(Math.abs(normalizedX * normalizedY), shape.cornerPower);
      position.setX(index, x * (1 - cornerPull));
      position.setY(index, y * (1 - cornerPull));
    }

    const puff =
      Math.cos((normalizedX * Math.PI) / 2) *
      Math.cos((normalizedY * Math.PI) / 2);
    const easedPuff = THREE.MathUtils.lerp(
      shape.rimRatio,
      1,
      Math.pow(Math.max(puff, 0), shape.puffExponent),
    );
    let puffedZ = z * easedPuff;

    if (wrinkleDepth > 0 && Math.abs(z) > thickness * 0.01) {
      // Frequency is normalized so the wrinkle count does not depend on how
      // large the pillow happens to be in world units.
      const wrinkle =
        Math.sin(normalizedX * Math.PI * shape.wrinkleFrequency) *
        Math.cos(normalizedY * Math.PI * shape.wrinkleFrequency) *
        wrinkleDepth *
        Math.max(puff, 0);
      puffedZ += z > 0 ? wrinkle : -wrinkle;
    }

    position.setZ(index, puffedZ);
  }

  position.needsUpdate = true;
  if (!shape.smoothSeams) {
    geometry.computeVertexNormals();
    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();
    return geometry;
  }

  const smoothedGeometry = weldBoxSeamVertices(geometry);
  geometry.dispose();
  smoothedGeometry.computeVertexNormals();
  smoothedGeometry.computeBoundingBox();
  smoothedGeometry.computeBoundingSphere();
  return smoothedGeometry;
}

/** Builds a plush pillow; `shape` overrides any default puff parameter. */
export function createPillowBufferGeometry(
  width: number,
  depth: number,
  thickness: number,
  segments: [number, number, number] = DEFAULT_PILLOW_SEGMENTS,
  shape: PillowShapeOptions = {},
): THREE.BufferGeometry {
  return createPuffedBoxGeometry(width, depth, thickness, segments, {
    ...PILLOW_SHAPE,
    ...shape,
  });
}

/**
 * Builds a box with rounded edges and corners for bedding (mattress, duvet).
 * Unlike the puffed pillow, the plan footprint is preserved exactly - faces
 * stay flat and only a radius around each edge curves in, so the part reads
 * as a rectangular slab with soft edges and still covers its legs.
 * `softness` sets the edge radius as a share of the smallest half extent.
 */
export function createSoftBoxBufferGeometry(
  width: number,
  depth: number,
  thickness: number,
  softness: number,
  segments: [number, number, number] = DEFAULT_SOFT_BOX_SEGMENTS,
): THREE.BufferGeometry {
  const clamped = THREE.MathUtils.clamp(softness, 0, 1);
  const halfWidth = width / 2;
  const halfDepth = depth / 2;
  const halfThickness = thickness / 2;
  const radius = clamped * Math.min(halfWidth, halfDepth, halfThickness);
  const innerX = halfWidth - radius;
  const innerY = halfDepth - radius;
  const innerZ = halfThickness - radius;
  const boxGeometry = new THREE.BoxGeometry(
    width,
    depth,
    thickness,
    segments[0],
    segments[1],
    segments[2],
  );
  const position = boxGeometry.attributes.position;

  for (let index = 0; index < position.count; index++) {
    // Concentrate vertices near the faces so the rounded zones get enough
    // points for a smooth arc while the flat faces stay sparsely sampled.
    const x = halfWidth * Math.sin((position.getX(index) / halfWidth) * (Math.PI / 2));
    const y = halfDepth * Math.sin((position.getY(index) / halfDepth) * (Math.PI / 2));
    const z = halfThickness * Math.sin((position.getZ(index) / halfThickness) * (Math.PI / 2));

    // Project points outside the inner box onto the rounding sphere/cylinder
    // around the nearest edge; points inside keep their exact position.
    const clampedX = THREE.MathUtils.clamp(x, -innerX, innerX);
    const clampedY = THREE.MathUtils.clamp(y, -innerY, innerY);
    const clampedZ = THREE.MathUtils.clamp(z, -innerZ, innerZ);
    const deltaX = x - clampedX;
    const deltaY = y - clampedY;
    const deltaZ = z - clampedZ;
    const deltaLength = Math.hypot(deltaX, deltaY, deltaZ);

    if (deltaLength > 1e-9) {
      const scale = radius / deltaLength;
      position.setXYZ(
        index,
        clampedX + deltaX * scale,
        clampedY + deltaY * scale,
        clampedZ + deltaZ * scale,
      );
    } else {
      position.setXYZ(index, x, y, z);
    }
  }

  position.needsUpdate = true;
  // Weld the duplicated box seam vertices so the rounded edges shade smoothly
  // instead of faceting, then recompute smooth normals across the welds.
  const geometry = weldBoxSeamVertices(boxGeometry);
  boxGeometry.dispose();
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

/**
 * Merges vertices that share the same position (a box keeps one vertex per
 * face at each seam) so smooth normals can flow across the rounded edges.
 *
 * Preserves UVs from the first welded vertex. Dropping them made textured
 * materials (oak, limestone) render as a single flat tint on lounge cushions
 * while sheen fabrics still looked intentional.
 */
function weldBoxSeamVertices(
  boxGeometry: THREE.BufferGeometry,
): THREE.BufferGeometry {
  const position = boxGeometry.attributes.position;
  const uv = boxGeometry.attributes.uv as THREE.BufferAttribute | undefined;
  const index = boxGeometry.index;
  const keyToWelded = new Map<string, number>();
  const weldedPositions: number[] = [];
  const weldedUvs: number[] | null = uv ? [] : null;
  const remap = new Uint32Array(position.count);

  for (let vertex = 0; vertex < position.count; vertex++) {
    const x = position.getX(vertex);
    const y = position.getY(vertex);
    const z = position.getZ(vertex);
    const key = `${x.toFixed(6)},${y.toFixed(6)},${z.toFixed(6)}`;
    let welded = keyToWelded.get(key);
    if (welded === undefined) {
      welded = weldedPositions.length / 3;
      keyToWelded.set(key, welded);
      weldedPositions.push(x, y, z);
      if (weldedUvs && uv) {
        weldedUvs.push(uv.getX(vertex), uv.getY(vertex));
      }
    }
    remap[vertex] = welded;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(weldedPositions, 3),
  );
  if (weldedUvs) {
    geometry.setAttribute("uv", new THREE.Float32BufferAttribute(weldedUvs, 2));
  }
  if (index) {
    const weldedIndex: number[] = [];
    for (let face = 0; face < index.count; face++) {
      weldedIndex.push(remap[index.getX(face)]);
    }
    geometry.setIndex(weldedIndex);
  }
  return geometry;
}
